export type MusicSubpageCategory = 'genre' | 'use-case' | 'mood';

export interface MusicSubpageFaq {
  question: string;
  answer: string;
}

export interface MusicDemoTrack {
  name: string;
  audioUrl: string;
  imageUrl: string;
  duration: string;
  prompt: string;
}

export interface MusicSubpage {
  slug: string;
  name: string;
  category: MusicSubpageCategory;
  seoTitle: string;
  seoDescription: string;
  heroHeading: string;
  heroSubheading: string;
  description: string;
  icon: string;
  gradient: string;
  faqs: MusicSubpageFaq[];
  relatedSlugs: string[];
  promptExamples: string[];
  demoTrack?: MusicDemoTrack;
}

export const music_subpages: MusicSubpage[] = [
  // ==================== GENRES ====================
  {
    slug: 'rock',
    name: 'Rock',
    category: 'genre',
    seoTitle: 'AI Rock Music Generator - Create Rock Songs & Instrumentals',
    seoDescription:
      'Generate rock music with AI. Create guitar-driven tracks, classic rock anthems, punk, alternative, and indie rock instrumentals in seconds. Free to try.',
    heroHeading: 'AI Rock Music Generator',
    heroSubheading:
      'Generate powerful rock tracks with driving guitars, thundering drums, and electrifying energy. From classic rock to punk and alternative.',
    description:
      'Our AI rock music generator creates authentic rock compositions across all sub-genres. Whether you need a classic rock anthem with soaring guitar solos, a gritty punk track with raw energy, or an alternative rock piece with atmospheric textures, the AI understands the nuances that make rock music compelling. Specify your preferred tempo, key, and instruments to get exactly the sound you envision.',
    icon: 'guitar',
    gradient: 'from-red-600/20 to-orange-600/20',
    faqs: [
      {
        question: 'Can the AI generate different rock sub-genres?',
        answer:
          'Yes, our AI can generate classic rock, punk rock, alternative rock, indie rock, hard rock, progressive rock, grunge, and more. Simply describe the sub-genre and style in your prompt to get the exact sound you want.',
      },
      {
        question: 'Can I specify guitar tones and effects for my rock tracks?',
        answer:
          'Absolutely. You can describe specific guitar characteristics in your prompt, such as distorted power chords, clean arpeggios, wah-wah effects, or acoustic fingerpicking. The AI will generate tracks that match your description.',
      },
      {
        question: 'Is the generated rock music suitable for commercial use?',
        answer:
          'Yes, all music generated with Easy-Peasy.AI is royalty-free and can be used in commercial projects including videos, games, podcasts, and more, depending on your subscription plan.',
      },
      {
        question: 'How long are the generated rock tracks?',
        answer:
          'Generated tracks are typically between 30 seconds and 2 minutes long, making them perfect for intros, background music, or as a starting point for longer compositions.',
      },
    ],
    relatedSlugs: ['metal', 'indie', 'pop', 'folk'],
    promptExamples: [
      'Classic rock anthem with heavy guitar riffs, powerful drums, and a soaring lead guitar solo in E minor at 130 BPM',
      'Indie rock track with jangly guitars, driving bass line, and energetic drums, upbeat and catchy, key of A major, 120 BPM',
      'Acoustic rock ballad with fingerpicked guitar, gentle percussion, and an emotional, melancholic feel in G major at 80 BPM',
    ],
    demoTrack: {
      name: 'Jingle and Sway',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/7a7cabda-b24e-47c5-a32f-9109d19ce0a8/cdbd6020-5059-4c30-96a8-cee63219894b.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/rock.jpeg',
      duration: '2:16',
      prompt:
        'Rock song with electric guitar riffs, groovy basslines, and a steady drumbeat',
    },
  },
  {
    slug: 'pop',
    name: 'Pop',
    category: 'genre',
    seoTitle: 'AI Pop Music Generator - Create Catchy Pop Tracks with AI',
    seoDescription:
      'Create catchy pop music with AI. Generate radio-ready pop tracks, synth-pop beats, and danceable pop songs instantly. No musical experience needed.',
    heroHeading: 'AI Pop Music Generator',
    heroSubheading:
      'Create catchy, radio-ready pop tracks with infectious melodies, polished production, and modern beats. From synth-pop to dance-pop and beyond.',
    description:
      'Our AI pop music generator produces polished, radio-ready tracks that capture the essence of modern pop music. Create songs with catchy hooks, driving beats, and contemporary production styles. Whether you need upbeat dance-pop, emotional ballads, or trendy synth-pop, the AI delivers professional-quality results that sound like they belong on a playlist.',
    icon: 'microphone',
    gradient: 'from-pink-600/20 to-purple-600/20',
    faqs: [
      {
        question: 'Can I create pop music in different styles?',
        answer:
          'Yes, you can generate various pop styles including synth-pop, dance-pop, indie pop, K-pop inspired tracks, electropop, and acoustic pop. Just describe the style you want in your prompt.',
      },
      {
        question: 'Will the generated pop music have vocals?',
        answer:
          'Currently, the AI generates instrumental pop tracks. These are perfect as backing tracks, background music, or for adding your own vocals on top.',
      },
      {
        question: 'Can I use AI-generated pop music for social media content?',
        answer:
          'Yes, the generated music is perfect for TikTok, Instagram Reels, YouTube videos, and other social media platforms. It is royalty-free based on your subscription plan.',
      },
      {
        question: 'How does the AI create catchy pop melodies?',
        answer:
          'The AI has been trained on a vast library of musical compositions and understands the patterns that make melodies memorable, including hook structures, chord progressions, and rhythmic patterns commonly found in hit pop songs.',
      },
    ],
    relatedSlugs: ['electronic', 'r-and-b', 'indie', 'latin'],
    promptExamples: [
      'Upbeat synth-pop track with catchy melody, modern production, punchy drums, and shimmering synths in C major at 125 BPM',
      'Emotional pop ballad with piano, soft strings, and a building crescendo, heartfelt and cinematic, key of F major, 72 BPM',
      'Dance-pop banger with heavy bass, tropical house elements, bright synths, and an infectious drop in B-flat major at 128 BPM',
    ],
    demoTrack: {
      name: 'Dancing Is My Love',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/4f5bda6c-cfbb-43bf-a06a-b8bf51e3a26a/81a3fb52-9815-4de2-a132-6f88c261cf1d.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/pop.jpeg',
      duration: '2:05',
      prompt:
        'Synth-pop with heavy 808s and driving beat, catchy and danceable',
    },
  },
  {
    slug: 'hip-hop',
    name: 'Hip-Hop',
    category: 'genre',
    seoTitle: 'AI Hip-Hop Beat Maker - Generate Hip-Hop Beats with AI',
    seoDescription:
      'Generate hip-hop beats and instrumentals with AI. Create boom-bap, trap, lo-fi hip-hop, and R&B beats instantly. Professional quality, royalty-free.',
    heroHeading: 'AI Hip-Hop Beat Generator',
    heroSubheading:
      'Create hard-hitting hip-hop beats with deep bass, crisp drums, and smooth melodies. From trap to boom-bap, lo-fi to drill.',
    description:
      'Our AI hip-hop beat generator produces authentic beats across all hip-hop sub-genres. Create booming trap beats with 808s and hi-hats, classic boom-bap with dusty samples and hard-hitting drums, or smooth lo-fi hip-hop perfect for studying. Specify your BPM, key, and instrumentation to craft the exact beat you need for your next project.',
    icon: 'headphones',
    gradient: 'from-yellow-600/20 to-orange-600/20',
    faqs: [
      {
        question: 'What hip-hop sub-genres can the AI produce?',
        answer:
          'The AI can generate trap, boom-bap, lo-fi hip-hop, drill, cloud rap, phonk, jazzy hip-hop, and many other sub-genres. Describe the style, tempo, and vibe in your prompt to get the sound you want.',
      },
      {
        question: 'Can I use these beats for my own rap songs?',
        answer:
          'Yes, the generated beats are royalty-free and can be used as backing tracks for your own vocals and lyrics, subject to your subscription plan terms.',
      },
      {
        question: 'Can the AI generate 808 bass patterns?',
        answer:
          'Yes, the AI excels at creating authentic 808 bass patterns, including sliding 808s, punchy kicks, and the deep sub-bass that defines modern hip-hop production.',
      },
      {
        question: 'What BPM range works best for hip-hop beats?',
        answer:
          'Hip-hop typically ranges from 60-100 BPM for boom-bap and lo-fi, 130-160 BPM for trap, and 140-150 BPM for drill. You can specify any tempo in your prompt.',
      },
    ],
    relatedSlugs: ['r-and-b', 'lo-fi', 'pop', 'electronic'],
    promptExamples: [
      'Smooth chill hip-hop beat with mellow piano melodies, deep 808 bass, and soft drums, perfect for a night drive, D minor at 90 BPM',
      'Hard-hitting trap beat with sliding 808s, rapid hi-hats, dark synth pads, and aggressive energy, F# minor at 145 BPM',
      'Jazzy boom-bap beat with sampled saxophone, vinyl crackle, punchy drums, and a head-nodding groove, B-flat minor at 85 BPM',
    ],
    demoTrack: {
      name: 'Hip-Hop Cypher',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/bceb71da-90de-4af2-97f2-a2ad7b8543de/9121606a-78ab-4709-91dc-66f067d4882a.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/hip-hop.jpeg',
      duration: '1:53',
      prompt:
        'Dark trap beat with heavy 808s and eerie bell plucks, hip-hop cypher energy',
    },
  },
  {
    slug: 'jazz',
    name: 'Jazz',
    category: 'genre',
    seoTitle: 'AI Jazz Music Generator - Create Jazz Compositions with AI',
    seoDescription:
      'Generate jazz music with AI. Create smooth jazz, bebop, swing, and fusion jazz tracks with piano, saxophone, and more. Professional quality.',
    heroHeading: 'AI Jazz Music Generator',
    heroSubheading:
      'Create sophisticated jazz compositions with smooth piano, warm saxophone, walking bass, and intricate drum patterns. From swing to fusion.',
    description:
      'Our AI jazz music generator captures the soul of jazz across its many forms. Generate smooth jazz for a relaxing ambiance, energetic bebop with complex harmonies, classic swing with a toe-tapping groove, or modern fusion that blends jazz with other genres. The AI understands jazz harmony, improvisation patterns, and the interplay between instruments that makes jazz timeless.',
    icon: 'saxophone',
    gradient: 'from-amber-600/20 to-yellow-600/20',
    faqs: [
      {
        question: 'Can the AI generate different jazz styles?',
        answer:
          'Yes, including smooth jazz, bebop, swing, cool jazz, jazz fusion, bossa nova, Latin jazz, modal jazz, and free jazz. Describe the style and instrumentation you want.',
      },
      {
        question: 'Will the jazz tracks sound authentic?',
        answer:
          'The AI has been trained to understand jazz harmony, swing feel, improvisation patterns, and the interplay between instruments. The results capture the essence of each jazz style.',
      },
      {
        question: 'Can I specify which instruments to feature?',
        answer:
          'Absolutely. You can request piano, saxophone, trumpet, double bass, guitar, vibraphone, or any combination. Simply mention the instruments in your prompt.',
      },
      {
        question:
          'Is AI-generated jazz good for restaurant or lounge background music?',
        answer:
          'Yes, AI-generated smooth jazz and bossa nova tracks are excellent for creating an elegant atmosphere in restaurants, lounges, cafes, and similar venues.',
      },
    ],
    relatedSlugs: ['lo-fi', 'r-and-b', 'classical', 'folk'],
    promptExamples: [
      'Smooth jazz track with warm saxophone melody, electric piano chords, walking bass, and brushed drums, relaxed and sophisticated, key of E-flat major at 110 BPM',
      'Upbeat swing jazz with piano trio, walking bass, and energetic brushwork, classic jazz club vibe, B-flat major at 160 BPM',
      'Jazz fusion with funky electric guitar, Rhodes piano, syncopated bass, and a groovy pocket, key of D minor at 120 BPM',
    ],
    demoTrack: {
      name: 'Streetlight Serenade',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/412daa02-0ac2-41b4-be6a-89918cd663c2/5fba788a-7ed3-469d-8005-42b206abd73c.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/jazz.jpeg',
      duration: '3:28',
      prompt:
        'Contemporary jazz with saxophone, harmonica, and gentle drumbeats',
    },
  },
  {
    slug: 'classical',
    name: 'Classical',
    category: 'genre',
    seoTitle: 'AI Classical Music Generator - Create Orchestral Compositions',
    seoDescription:
      'Generate classical music with AI. Create orchestral compositions, piano pieces, string quartets, and symphonic arrangements. Elegant and professional.',
    heroHeading: 'AI Classical Music Generator',
    heroSubheading:
      'Create elegant orchestral compositions, delicate piano pieces, and sweeping string arrangements. From baroque to romantic to contemporary classical.',
    description:
      'Our AI classical music generator produces sophisticated compositions that honor the traditions of classical music while making creation accessible to everyone. Generate sweeping orchestral arrangements, intimate piano sonatas, delicate string quartets, or dramatic symphonic movements. Perfect for film scores, presentations, events, or simply enjoying the beauty of orchestral music.',
    icon: 'piano',
    gradient: 'from-indigo-600/20 to-blue-600/20',
    faqs: [
      {
        question: 'What types of classical music can the AI compose?',
        answer:
          'The AI can generate orchestral symphonies, piano sonatas, string quartets, chamber music, baroque pieces, romantic era compositions, minimalist works, and contemporary classical music.',
      },
      {
        question: 'Can I use classical AI music for film or video projects?',
        answer:
          'Yes, AI-generated classical music is excellent for film scores, documentaries, trailers, corporate videos, and presentations. The music is royalty-free based on your plan.',
      },
      {
        question: 'Can the AI compose for specific classical instruments?',
        answer:
          'Yes, you can request compositions featuring piano, violin, cello, flute, oboe, full orchestra, chamber ensemble, or any combination of classical instruments.',
      },
      {
        question: 'How does AI classical music compare to human compositions?',
        answer:
          'AI-generated classical music captures the harmonic structures, dynamic range, and emotional qualities of classical compositions. While it may not replace a human composer for complex symphonies, it produces beautiful, usable music for most purposes.',
      },
    ],
    relatedSlugs: ['ambient', 'jazz', 'for-film', 'epic-music'],
    promptExamples: [
      'Elegant piano sonata in the style of Chopin, with flowing arpeggios, expressive dynamics, and a melancholic beauty, D-flat major at 72 BPM',
      'Sweeping orchestral piece with full strings, brass fanfares, and timpani, dramatic and triumphant, building to a powerful climax, C major at 100 BPM',
      'Gentle string quartet with delicate violin melody, warm harmonies, and a peaceful, pastoral atmosphere, G major at 60 BPM',
    ],
    demoTrack: {
      name: 'Storm Over Vienna',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/2e073183-b13f-4578-9768-31d61a303af3/3e2ac577-31eb-49b6-bff4-1d222369e4ff.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/classical.jpeg',
      duration: '3:04',
      prompt:
        'Classical orchestral piece in Beethoven style, dramatic with bold motifs',
    },
  },
  {
    slug: 'electronic',
    name: 'Electronic',
    category: 'genre',
    seoTitle: 'AI Electronic Music Generator - Create EDM & Synth Tracks',
    seoDescription:
      'Generate electronic music with AI. Create EDM, house, techno, trance, dubstep, and synthwave tracks with professional production quality. Free to try.',
    heroHeading: 'AI Electronic Music Generator',
    heroSubheading:
      'Create pulsating electronic tracks with powerful synths, driving beats, and euphoric drops. From house and techno to trance and synthwave.',
    description:
      'Our AI electronic music generator produces club-ready tracks across the full spectrum of electronic music. Create thumping house beats, hypnotic techno grooves, euphoric trance anthems, heavy dubstep drops, or retro synthwave vibes. The AI understands the production techniques, sound design, and arrangement patterns that define electronic music.',
    icon: 'synth',
    gradient: 'from-cyan-600/20 to-blue-600/20',
    faqs: [
      {
        question: 'What electronic music sub-genres can the AI create?',
        answer:
          'The AI can generate house, techno, trance, dubstep, drum and bass, synthwave, ambient electronic, progressive, deep house, electro, and many more sub-genres.',
      },
      {
        question: 'Can I control the BPM and energy level?',
        answer:
          'Yes, you can specify exact BPM, energy level, and build-up/drop patterns in your prompt. Electronic music typically ranges from 100 BPM (downtempo) to 175 BPM (drum and bass).',
      },
      {
        question: 'Will the tracks have professional sound design?',
        answer:
          'The AI generates tracks with rich synthesizer textures, well-crafted bass lines, crisp percussion, and the sonic depth expected in professional electronic music production.',
      },
      {
        question: 'Can I use the electronic tracks for DJ sets or live events?',
        answer:
          'Yes, the generated tracks are yours to use in DJ sets, live performances, and events, subject to your subscription plan terms.',
      },
    ],
    relatedSlugs: ['pop', 'hip-hop', 'ambient', 'for-games'],
    promptExamples: [
      'Energetic progressive house track with building synth layers, punchy kick drums, and a euphoric melodic drop, A minor at 128 BPM',
      'Dark techno with driving bassline, industrial textures, hypnotic percussion, and a relentless groove, E minor at 135 BPM',
      'Retro synthwave with warm analog synths, arpeggiated melodies, gated reverb drums, and a nostalgic 80s atmosphere, F minor at 110 BPM',
    ],
    demoTrack: {
      name: 'The Pulse and the Command',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/28372dd5-ec05-4939-96fa-f38dcb130cda/c85a39d8-61ff-4f87-b782-ff1cda233de0.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/electronic.jpeg',
      duration: '7:20',
      prompt: 'Industrial hypno-techno meets dark psy-trance, 120-140 BPM',
    },
  },
  {
    slug: 'lo-fi',
    name: 'Lo-Fi',
    category: 'genre',
    seoTitle: 'AI Lo-Fi Music Generator - Create Lo-Fi Beats & Chill Hop',
    seoDescription:
      'Generate lo-fi music with AI. Create lo-fi hip-hop beats, chill study music, and relaxing lo-fi tracks with vinyl warmth. Perfect for studying and relaxing.',
    heroHeading: 'AI Lo-Fi Music Generator',
    heroSubheading:
      'Create warm, nostalgic lo-fi beats with vinyl crackle, mellow piano, and laid-back drums. Perfect for studying, relaxing, and focusing.',
    description:
      'Our AI lo-fi music generator captures the cozy, nostalgic aesthetic that defines lo-fi hip-hop and chill hop. Generate tracks with warm vinyl crackle, mellow piano chords, jazzy guitar licks, soft drum patterns, and that characteristic lo-fi warmth. Perfect for study playlists, background ambiance, or content creation where you need a relaxing, unobtrusive soundtrack.',
    icon: 'vinyl',
    gradient: 'from-orange-600/20 to-amber-600/20',
    faqs: [
      {
        question: 'What makes lo-fi music different from regular hip-hop?',
        answer:
          'Lo-fi music is characterized by intentionally low-fidelity production elements like vinyl crackle, tape hiss, muffled drums, and warm, slightly distorted textures. It creates a nostalgic, cozy atmosphere perfect for relaxation.',
      },
      {
        question: 'Is lo-fi music good for studying?',
        answer:
          'Yes, lo-fi music is widely recognized as excellent study music. Its repetitive, mellow nature helps maintain focus without being distracting, which is why lo-fi study streams are so popular.',
      },
      {
        question: 'Can I create lo-fi playlists for my cafe or business?',
        answer:
          'Absolutely. AI-generated lo-fi music is perfect for cafes, bookstores, co-working spaces, and any environment where you want a relaxing atmosphere. The music is royalty-free based on your plan.',
      },
      {
        question: 'What instruments work best in lo-fi prompts?',
        answer:
          'Common lo-fi instruments include piano, Rhodes electric piano, jazz guitar, soft drums with vinyl texture, mellow bass, and ambient pads. Mentioning vinyl crackle or tape warmth in your prompt enhances the lo-fi aesthetic.',
      },
    ],
    relatedSlugs: ['hip-hop', 'jazz', 'ambient', 'chill-music'],
    promptExamples: [
      'Lo-fi hip-hop beat with warm piano chords, vinyl crackle, soft kick and snare, mellow bass, and a cozy nighttime study vibe, D minor at 75 BPM',
      'Chill lo-fi track with jazzy guitar, Rhodes piano, tape hiss, gentle rain ambiance, and a laid-back head-nodding groove, C major at 80 BPM',
      'Lo-fi beat with anime-inspired vibes, nostalgic chords, soft bells, dusty drums, and a wistful, dreamy atmosphere, G minor at 70 BPM',
    ],
    demoTrack: {
      name: 'Coffee Cup Karma',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/46bec11e-4566-43df-970b-6f2b72367616/4121ccf4-7049-4838-82c8-81fa2c001c83.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/lo-fi.jpeg',
      duration: '3:09',
      prompt:
        'Warm lo-fi groove with brushed drums, upright bass, and cozy electric piano',
    },
  },
  {
    slug: 'country',
    name: 'Country',
    category: 'genre',
    seoTitle: 'AI Country Music Generator - Create Country Songs with AI',
    seoDescription:
      'Generate country music with AI. Create country, bluegrass, Americana, and country-pop tracks with guitar, banjo, and fiddle. Free to try.',
    heroHeading: 'AI Country Music Generator',
    heroSubheading:
      'Create authentic country tracks with acoustic guitar, fiddle, steel guitar, and heartfelt melodies. From classic country to modern country-pop.',
    description:
      'Our AI country music generator produces authentic country compositions that capture the spirit of Nashville and beyond. Generate classic country with twangy guitars and storytelling melodies, modern country-pop with polished production, bluegrass with driving banjo and fiddle, or Americana with folk-influenced arrangements. Perfect for content creators, videographers, and anyone who loves country music.',
    icon: 'banjo',
    gradient: 'from-yellow-700/20 to-orange-700/20',
    faqs: [
      {
        question: 'What country sub-genres can the AI generate?',
        answer:
          'The AI can create classic country, country-pop, bluegrass, Americana, outlaw country, country rock, western swing, and modern Nashville-style tracks.',
      },
      {
        question: 'Can I get fiddle and steel guitar in the tracks?',
        answer:
          'Yes, simply mention the instruments you want in your prompt. The AI can generate tracks featuring fiddle, pedal steel guitar, banjo, acoustic guitar, mandolin, dobro, and other traditional country instruments.',
      },
      {
        question: 'Is AI country music suitable for line dancing or events?',
        answer:
          'Absolutely. You can generate upbeat country tracks perfect for line dancing, barn dances, country-themed events, and celebrations.',
      },
      {
        question: 'Can I create country music for my YouTube or podcast?',
        answer:
          'Yes, the generated country music is royalty-free and ideal for YouTube intros, podcast backgrounds, travel vlogs, cooking shows, and other content.',
      },
    ],
    relatedSlugs: ['folk', 'rock', 'pop', 'indie'],
    promptExamples: [
      'Classic country ballad with acoustic guitar, pedal steel guitar, gentle fiddle, and a warm, nostalgic storytelling feel, G major at 95 BPM',
      'Upbeat country-pop with driving acoustic guitar, electric lead, modern drums, and a fun, sing-along vibe, D major at 130 BPM',
      'Bluegrass instrumental with fast banjo picking, fiddle melody, mandolin chops, and upright bass, energetic and joyful, A major at 140 BPM',
    ],
    demoTrack: {
      name: 'Vartin Vaikkari',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/5321e250-3ffb-4719-a494-5d322268bc63/4157159c-a811-4a75-a7be-a78e067f4792.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/for-podcasts.jpeg',
      duration: '0:53',
      prompt:
        'Upbeat country with acoustic guitar and fiddle, melodic and cheerful',
    },
  },
  {
    slug: 'ambient',
    name: 'Ambient',
    category: 'genre',
    seoTitle: 'AI Ambient Music Generator - Create Ambient Soundscapes',
    seoDescription:
      'Generate ambient music with AI. Create atmospheric soundscapes, drone music, space ambient, and relaxing ambient textures. Perfect for meditation and focus.',
    heroHeading: 'AI Ambient Music Generator',
    heroSubheading:
      'Create immersive ambient soundscapes with ethereal textures, atmospheric pads, and evolving sonic landscapes. Perfect for meditation, focus, and relaxation.',
    description:
      'Our AI ambient music generator creates immersive soundscapes that transport listeners to other worlds. Generate floating atmospheric textures, deep drone compositions, shimmering space ambient, nature-infused environments, or minimalist ambient pieces. Ideal for meditation, yoga, sleep, creative work, or adding atmosphere to films, games, and installations.',
    icon: 'waves',
    gradient: 'from-teal-600/20 to-cyan-600/20',
    faqs: [
      {
        question: 'What types of ambient music can the AI create?',
        answer:
          'The AI can generate dark ambient, space ambient, nature ambient, drone music, ambient electronic, new age, minimalist ambient, and atmospheric textures.',
      },
      {
        question: 'Is ambient music good for sleep and relaxation?',
        answer:
          'Yes, ambient music is widely used for sleep, relaxation, and stress relief. The gentle, evolving textures create a calming environment without sudden changes that might disrupt rest.',
      },
      {
        question: 'Can I create hours of ambient music for background use?',
        answer:
          'While each generation produces a track of limited length, you can generate multiple ambient pieces and combine them for longer listening sessions. The consistent quality makes transitions seamless.',
      },
      {
        question: 'Is AI ambient music suitable for spas and wellness centers?',
        answer:
          'Absolutely. AI-generated ambient music is perfect for spas, wellness centers, yoga studios, massage rooms, and therapy practices where a peaceful sonic environment is essential.',
      },
    ],
    relatedSlugs: ['lo-fi', 'classical', 'for-meditation', 'chill-music'],
    promptExamples: [
      'Ethereal ambient soundscape with slowly evolving synth pads, shimmering textures, and a vast, spacious atmosphere, C major, very slow and meditative',
      'Dark ambient with deep drones, distant echoes, metallic textures, and an eerie, mysterious atmosphere, no defined key, 60 BPM',
      'Nature-inspired ambient with soft wind sounds, gentle piano notes, warm pad layers, and birdsong elements, F major, peaceful and flowing',
    ],
    demoTrack: {
      name: 'Whispers of the Moon',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/103038d5-3be6-4cf5-bd20-617159c04834/f21b9d38-65b3-4fc5-91eb-cbb230cc4c0c.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/ambient.jpeg',
      duration: '3:53',
      prompt:
        'Calm ambient with gentle oud, ambient strings, and ethereal soundscape',
    },
  },
  {
    slug: 'r-and-b',
    name: 'R&B',
    category: 'genre',
    seoTitle: 'AI R&B Music Generator - Create R&B Beats & Soul Tracks',
    seoDescription:
      'Generate R&B music with AI. Create smooth R&B beats, neo-soul tracks, and contemporary R&B instrumentals with rich harmonies. Professional quality.',
    heroHeading: 'AI R&B Music Generator',
    heroSubheading:
      'Create smooth R&B tracks with silky melodies, rich harmonies, and groovy rhythms. From classic soul to modern neo-soul and contemporary R&B.',
    description:
      'Our AI R&B music generator produces soulful, groove-driven tracks that capture the smooth sophistication of R&B. Generate contemporary R&B with modern production, classic soul with warm vintage tones, neo-soul with jazzy harmonies, or slow jams with romantic atmosphere. The AI understands the lush chord progressions, rhythmic patterns, and melodic sensibilities that define R&B.',
    icon: 'heart',
    gradient: 'from-purple-600/20 to-pink-600/20',
    faqs: [
      {
        question: 'What R&B styles can the AI generate?',
        answer:
          'The AI can create contemporary R&B, classic soul, neo-soul, slow jams, alternative R&B, funk-influenced R&B, and gospel-influenced soul music.',
      },
      {
        question: 'Can I use R&B beats for singing over?',
        answer:
          'Yes, the instrumental R&B tracks are perfect as backing tracks for vocals. They feature well-crafted chord progressions and arrangements that leave space for vocal performance.',
      },
      {
        question: 'Does the AI capture the groove and feel of real R&B?',
        answer:
          'The AI understands R&B groove, syncopation, chord voicings, and the warm, lush production that defines the genre. The results have authentic feel and musicality.',
      },
      {
        question: 'Can I create R&B music for romantic occasions?',
        answer:
          'Absolutely. Generate slow jams, romantic R&B ballads, and soulful love songs perfect for weddings, anniversaries, date nights, or romantic content.',
      },
    ],
    relatedSlugs: ['hip-hop', 'jazz', 'pop', 'lo-fi'],
    promptExamples: [
      'Smooth contemporary R&B with silky synth pads, warm bass, crisp drums, and a romantic late-night vibe, A-flat major at 85 BPM',
      'Neo-soul track with Rhodes piano, warm guitar licks, deep bass groove, and a jazzy, laid-back atmosphere, D minor at 95 BPM',
      'Classic soul instrumental with vintage keys, horns section, walking bass, and a feel-good Motown groove, B-flat major at 110 BPM',
    ],
    demoTrack: {
      name: 'Ready for Love',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/f06b73f0-ee42-4eda-8e95-d466938545b8/9c94fb1c-10e0-43cf-8995-0c9bd1b18c93.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/r-and-b.jpeg',
      duration: '2:45',
      prompt:
        'Neo soul / R&B with warm electric piano, lush basslines, and smooth vocals',
    },
  },
  {
    slug: 'metal',
    name: 'Metal',
    category: 'genre',
    seoTitle: 'AI Metal Music Generator - Create Heavy Metal Tracks with AI',
    seoDescription:
      'Generate metal music with AI. Create heavy metal, thrash, death metal, and progressive metal tracks with crushing guitars and powerful drums. Free to try.',
    heroHeading: 'AI Metal Music Generator',
    heroSubheading:
      'Create crushing metal tracks with distorted guitars, thundering double bass drums, and relentless energy. From heavy metal to thrash and progressive.',
    description:
      'Our AI metal music generator creates intense, powerful compositions across the spectrum of metal music. Generate heavy metal with crushing riffs and soaring solos, thrash metal with breakneck speed and aggression, progressive metal with complex time signatures and intricate arrangements, or melodic metal with harmonized guitars and epic atmosphere. The AI captures the raw power and technical precision that defines metal.',
    icon: 'lightning',
    gradient: 'from-gray-600/20 to-red-800/20',
    faqs: [
      {
        question: 'What metal sub-genres does the AI support?',
        answer:
          'The AI can generate heavy metal, thrash metal, death metal, black metal, progressive metal, power metal, melodic metal, metalcore, and doom metal, among others.',
      },
      {
        question: 'Can the AI create complex metal guitar riffs?',
        answer:
          'Yes, the AI understands metal guitar techniques including palm-muted riffs, tremolo picking, sweep arpeggios, harmonized leads, and complex rhythmic patterns.',
      },
      {
        question: 'Can I specify double bass drum patterns?',
        answer:
          'Absolutely. Mention double bass drums, blast beats, or specific drumming styles in your prompt. The AI generates authentic metal percussion patterns.',
      },
      {
        question: 'Is AI metal music good for gaming content?',
        answer:
          'Yes, metal music is extremely popular for gaming montages, boss battle themes, action sequences, and high-energy gaming content.',
      },
    ],
    relatedSlugs: ['rock', 'electronic', 'epic-music', 'dramatic-music'],
    promptExamples: [
      'Heavy thrash metal with fast palm-muted riffs, double bass drums, aggressive energy, and a shredding guitar solo, E minor at 180 BPM',
      'Epic power metal with soaring harmonized guitars, symphonic elements, galloping rhythm, and a triumphant atmosphere, D minor at 160 BPM',
      'Progressive metal with complex time signatures, technical guitar work, atmospheric clean sections, and a dynamic build, B minor at 140 BPM',
    ],
    demoTrack: {
      name: 'Reborn in Chaos',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/b0abffd0-c84f-431c-8dab-a459a0f119fb/6fcc6d12-69d4-44d5-b308-474bd300a2aa.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/metal.jpeg',
      duration: '2:09',
      prompt:
        'Death metal with double-kick drums, distorted guitars, and chaotic tempo shifts',
    },
  },
  {
    slug: 'folk',
    name: 'Folk',
    category: 'genre',
    seoTitle: 'AI Folk Music Generator - Create Folk & Acoustic Music',
    seoDescription:
      'Generate folk music with AI. Create acoustic folk, Celtic, Americana, and world folk tracks with guitar, violin, and traditional instruments.',
    heroHeading: 'AI Folk Music Generator',
    heroSubheading:
      'Create heartfelt folk music with acoustic instruments, warm melodies, and storytelling tradition. From Celtic to Americana and world folk.',
    description:
      'Our AI folk music generator produces authentic folk compositions that honor musical traditions from around the world. Generate acoustic folk with fingerpicked guitar and harmonica, Celtic folk with fiddle and tin whistle, Americana with mandolin and banjo, or world folk incorporating instruments and scales from various cultures. Perfect for storytelling, documentaries, travel content, and creating a warm, authentic atmosphere.',
    icon: 'acoustic',
    gradient: 'from-green-700/20 to-amber-700/20',
    faqs: [
      {
        question: 'What folk music traditions can the AI represent?',
        answer:
          'The AI can generate American folk, Celtic/Irish folk, Scandinavian folk, Balkan folk, Americana, bluegrass-influenced folk, and various world folk traditions.',
      },
      {
        question: 'Can the AI use traditional folk instruments?',
        answer:
          'Yes, you can request acoustic guitar, fiddle, mandolin, banjo, tin whistle, accordion, harp, bodhr\u00e1n, and other traditional instruments in your prompt.',
      },
      {
        question: 'Is folk music good for documentary or travel videos?',
        answer:
          'Absolutely. Folk music adds authenticity and emotional depth to documentaries, travel vlogs, nature films, and cultural content.',
      },
      {
        question: 'Can I generate Celtic music specifically?',
        answer:
          'Yes, describe Celtic elements in your prompt such as fiddle, tin whistle, Celtic harp, jig or reel rhythms, and the AI will generate authentic Celtic-inspired folk music.',
      },
    ],
    relatedSlugs: ['country', 'indie', 'classical', 'ambient'],
    promptExamples: [
      'Warm acoustic folk with fingerpicked guitar, gentle harmonica, soft percussion, and a campfire storytelling feel, C major at 100 BPM',
      'Celtic folk jig with fiddle melody, tin whistle harmonies, bodhr\u00e1n rhythm, and an energetic, danceable groove, D major at 130 BPM',
      'Melancholic Americana with slide guitar, gentle banjo, upright bass, and a vast, open landscape atmosphere, A minor at 85 BPM',
    ],
    demoTrack: {
      name: 'Duschkopf-Diva',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/8ea63875-03fa-4faf-973f-67e655479ce8/568330f0-5aae-4100-91d4-0e5dc26d4236.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/folk.jpeg',
      duration: '2:57',
      prompt:
        'Celtic folk with harp, tin whistle, bodhran, and layered harmonies',
    },
  },
  {
    slug: 'latin',
    name: 'Latin',
    category: 'genre',
    seoTitle: 'AI Latin Music Generator - Create Salsa, Reggaeton & Bossa Nova',
    seoDescription:
      'Generate Latin music with AI. Create salsa, reggaeton, bossa nova, cumbia, and Latin pop tracks with authentic rhythms and instruments.',
    heroHeading: 'AI Latin Music Generator',
    heroSubheading:
      'Create vibrant Latin music with infectious rhythms, passionate melodies, and authentic Latin grooves. From salsa and reggaeton to bossa nova.',
    description:
      'Our AI Latin music generator captures the rhythm and passion of Latin music across its many vibrant styles. Generate fiery salsa with dynamic percussion and horn sections, modern reggaeton with dembow beats, elegant bossa nova with nylon guitar, or cumbia with its infectious accordion and rhythm patterns. The AI understands the complex rhythmic structures and melodic traditions of Latin music.',
    icon: 'maracas',
    gradient: 'from-red-600/20 to-yellow-600/20',
    faqs: [
      {
        question: 'What Latin music styles can the AI create?',
        answer:
          'The AI can generate salsa, reggaeton, bossa nova, cumbia, bachata, merengue, Latin pop, Latin jazz, tango, and various other Latin American and Caribbean music styles.',
      },
      {
        question: 'Can the AI capture authentic Latin rhythms?',
        answer:
          'Yes, the AI understands clave patterns, dembow rhythms, bossa nova grooves, salsa montunos, and other essential Latin rhythmic patterns that give each style its characteristic feel.',
      },
      {
        question: 'Is Latin AI music good for dance events?',
        answer:
          'Absolutely. You can generate energetic salsa, bachata, reggaeton, and cumbia tracks perfect for dance parties, fitness classes, and social events.',
      },
      {
        question: 'Can I create background music for a Latin restaurant?',
        answer:
          'Yes, generate bossa nova, Latin jazz, or soft Latin pop for an authentic, welcoming atmosphere in restaurants, bars, and hospitality venues.',
      },
    ],
    relatedSlugs: ['pop', 'jazz', 'reggae', 'hip-hop'],
    promptExamples: [
      'Energetic salsa track with bright horn section, driving conga and timbale rhythms, piano montuno, and a lively, danceable groove, C major at 180 BPM',
      'Smooth bossa nova with nylon guitar, soft percussion, gentle bass, and a relaxed, romantic Brazilian cafe atmosphere, F major at 130 BPM',
      'Modern reggaeton with punchy dembow beat, deep bass, catchy synth melody, and a club-ready energy, A minor at 95 BPM',
    ],
    demoTrack: {
      name: 'Reggaeton Playero',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/cad37a57-393c-4a07-a1cc-b0fe6b2c4fba/80dd228d-5264-47d1-8d52-cbe4c446005b.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/latin.jpeg',
      duration: '2:45',
      prompt:
        'Reggaeton playero with chill summer vibes, guitars, ukelele, and smooth rhythm',
    },
  },
  {
    slug: 'indie',
    name: 'Indie',
    category: 'genre',
    seoTitle: 'AI Indie Music Generator - Create Indie Rock & Indie Pop',
    seoDescription:
      'Generate indie music with AI. Create indie rock, indie pop, indie folk, and dream pop tracks with unique textures and authentic indie aesthetics.',
    heroHeading: 'AI Indie Music Generator',
    heroSubheading:
      'Create distinctive indie tracks with textured guitars, dreamy atmospheres, and unique sonic character. From indie rock and indie pop to shoegaze and dream pop.',
    description:
      'Our AI indie music generator produces tracks with the creative spirit and sonic uniqueness that defines independent music. Generate jangly indie rock with textured guitar layers, dreamy indie pop with shimmering synths and airy vocals, atmospheric shoegaze with walls of reverb, or intimate indie folk with stripped-back arrangements. Perfect for filmmakers, content creators, and anyone seeking music with character and depth.',
    icon: 'star',
    gradient: 'from-violet-600/20 to-indigo-600/20',
    faqs: [
      {
        question: 'What indie sub-genres can the AI produce?',
        answer:
          'The AI can generate indie rock, indie pop, indie folk, dream pop, shoegaze, post-punk, lo-fi indie, math rock, emo, and chamber pop, among others.',
      },
      {
        question:
          'Can the AI capture the lo-fi, textured sound of indie music?',
        answer:
          'Yes, the AI understands the production aesthetics of indie music, including reverb-drenched guitars, lo-fi textures, layered arrangements, and the characteristic sonic warmth of indie recordings.',
      },
      {
        question: 'Is indie music good for short films and indie games?',
        answer:
          'Absolutely. Indie music pairs perfectly with independent films, short films, art house projects, indie games, and creative content that benefits from unique, non-mainstream sound.',
      },
      {
        question: 'Can I create music similar to specific indie bands?',
        answer:
          'While the AI does not copy specific artists, you can describe the sonic qualities you want, such as dreamy shoegaze guitars, jangly post-punk rhythms, or ethereal dream pop textures, and the AI will generate music with those characteristics.',
      },
    ],
    relatedSlugs: ['rock', 'folk', 'pop', 'lo-fi'],
    promptExamples: [
      'Dream pop with shimmering reverb guitars, ethereal synth pads, soft vocals-style melody, and a hazy, nostalgic atmosphere, E major at 115 BPM',
      'Indie rock with jangly guitars, driving bass, energetic drums, and a bright, optimistic feel with a catchy hook, A major at 135 BPM',
      'Shoegaze with massive wall of distorted guitar, swirling effects, heavy reverb, and a beautiful, overwhelming sonic texture, D minor at 90 BPM',
    ],
    demoTrack: {
      name: 'Fragments of Me',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/6d72a744-b8b1-4ad0-b73d-42528114323a/33c521e4-b79f-435c-946c-bbaa5adc14a8.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/indie.jpeg',
      duration: '2:52',
      prompt:
        'Indie pop with soft synths and acoustic guitar, intimate vocals with layered harmonies',
    },
  },
  {
    slug: 'reggae',
    name: 'Reggae',
    category: 'genre',
    seoTitle: 'AI Reggae Music Generator - Create Reggae & Dub Tracks',
    seoDescription:
      'Generate reggae music with AI. Create reggae, dub, ska, and dancehall tracks with authentic offbeat rhythms, deep bass, and island vibes.',
    heroHeading: 'AI Reggae Music Generator',
    heroSubheading:
      'Create authentic reggae tracks with offbeat rhythms, deep bass, and that unmistakable island groove. From roots reggae and dub to ska and dancehall.',
    description:
      'Our AI reggae music generator captures the soul of Jamaican music and its global influence. Generate roots reggae with conscious vibes and classic rhythms, heavy dub with spacey effects and deep bass, upbeat ska with energetic brass, or modern dancehall with digital riddims. The AI understands the offbeat guitar skank, one-drop drum patterns, and bass-heavy mix that defines reggae.',
    icon: 'sun',
    gradient: 'from-green-600/20 to-yellow-600/20',
    faqs: [
      {
        question: 'What reggae styles can the AI generate?',
        answer:
          'The AI can create roots reggae, dub, ska, rocksteady, dancehall, lovers rock, reggae fusion, and digital reggae tracks.',
      },
      {
        question: 'Can the AI capture authentic reggae rhythms?',
        answer:
          'Yes, the AI understands one-drop rhythms, offbeat guitar skanks, bass-heavy grooves, and the rhythmic patterns essential to authentic reggae music.',
      },
      {
        question: 'Can I create dub music with echo and reverb effects?',
        answer:
          'Absolutely. Mention dub-style production in your prompt, and the AI will generate tracks with heavy reverb, delay effects, echo drops, and the spacey, hypnotic quality that defines dub.',
      },
      {
        question: 'Is reggae music good for summer or beach-themed content?',
        answer:
          'Yes, reggae music is perfect for tropical, beach, travel, and summer-themed videos, ads, and events. Its positive, laid-back energy instantly creates a warm, island atmosphere.',
      },
    ],
    relatedSlugs: ['latin', 'hip-hop', 'folk', 'chill-music'],
    promptExamples: [
      'Classic roots reggae with offbeat guitar skank, deep bass groove, one-drop drums, and warm organ chords, G major at 75 BPM',
      'Heavy dub track with deep sub-bass, echoing percussion, reverb-drenched guitar stabs, and a spacey, hypnotic atmosphere, A minor at 70 BPM',
      'Upbeat ska with bright horn section, fast offbeat guitar, driving bass, and an energetic, danceable feel, D major at 160 BPM',
    ],
    demoTrack: {
      name: 'Radio Doubleyou One',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/05095924-7477-4388-852a-b00036a9e92f/d180e6f7-4ae3-45ae-8500-47c7ac2d629b.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/reggae.jpeg',
      duration: '0:18',
      prompt:
        'Reggae-ska jingle with bright brass stabs, offbeat guitar chops, tight bass',
    },
  },

  // ==================== USE CASES ====================
  {
    slug: 'for-videos',
    name: 'Music for Videos',
    category: 'use-case',
    seoTitle: 'AI Music Generator for Videos - Background Music for YouTube',
    seoDescription:
      'Create royalty-free background music for videos with AI. Generate custom soundtracks for YouTube, TikTok, commercials, and social media content. Free to try.',
    heroHeading: 'AI Music Generator for Videos',
    heroSubheading:
      'Generate custom, royalty-free background music perfectly tailored to your video content. From YouTube and TikTok to commercials and documentaries.',
    description:
      'Finding the right music for your videos can be time-consuming and expensive. Our AI music generator creates custom soundtracks tailored to your specific video content in seconds. Whether you need upbeat music for a YouTube vlog, dramatic underscore for a documentary, energetic beats for social media, or elegant music for a corporate presentation, the AI generates the perfect soundtrack every time.',
    icon: 'video',
    gradient: 'from-blue-600/20 to-cyan-600/20',
    faqs: [
      {
        question: 'Is AI-generated music safe to use on YouTube?',
        answer:
          'Yes, music generated with Easy-Peasy.AI is royalty-free and will not trigger Content ID claims on YouTube. You can monetize your videos without worrying about copyright issues.',
      },
      {
        question: 'Can I create music that matches my video mood?',
        answer:
          'Absolutely. Describe the mood, energy level, and style you need in your prompt. Whether it is upbeat and energetic, calm and reflective, or dramatic and intense, the AI will match your vision.',
      },
      {
        question: 'What video types work best with AI music?',
        answer:
          'AI-generated music works excellently for YouTube videos, TikTok and Reels, commercials, corporate videos, vlogs, tutorials, documentaries, wedding videos, and any other video content.',
      },
      {
        question: 'Can I generate music of a specific length for my video?',
        answer:
          'The AI generates tracks of a fixed length, but you can easily trim or loop them in your video editor to match your exact timing needs.',
      },
    ],
    relatedSlugs: ['for-podcasts', 'for-film', 'for-games', 'pop'],
    promptExamples: [
      'Upbeat, positive background music for a YouTube travel vlog, acoustic guitar and light percussion, bright and inspiring, C major at 120 BPM',
      'Corporate presentation music with gentle piano, soft strings, and a professional, confident atmosphere, G major at 100 BPM',
      'Energetic social media music with modern beats, catchy synth hooks, and a trendy, attention-grabbing feel, A minor at 128 BPM',
    ],
    demoTrack: {
      name: 'Slim Now Anthem',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/6dc71ffa-3861-4307-8a64-841a263fa1d3/f38c0203-286b-40f5-8ac4-15c6a33f6f28.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/for-videos.jpeg',
      duration: '2:30',
      prompt:
        'Pop-electronic with driving beat and sparkling synth layers, positive commercial vibe',
    },
  },
  {
    slug: 'for-games',
    name: 'Music for Games',
    category: 'use-case',
    seoTitle: 'AI Music Generator for Games - Game Soundtrack Creator',
    seoDescription:
      'Create video game music with AI. Generate game soundtracks, boss battle themes, menu music, and level background tracks. Perfect for indie game developers.',
    heroHeading: 'AI Music for Game Development',
    heroSubheading:
      'Create immersive game soundtracks with AI. From epic boss battles to peaceful menu screens, generate the perfect music for every game moment.',
    description:
      'Game music sets the emotional tone for every moment of gameplay. Our AI music generator helps game developers create custom soundtracks without the cost of hiring a composer. Generate epic boss battle themes, peaceful exploration music, tense stealth sequences, triumphant victory fanfares, or ambient menu screen music. Whether you are building an indie game or prototyping a larger project, get professional game audio instantly.',
    icon: 'gamepad',
    gradient: 'from-purple-600/20 to-pink-600/20',
    faqs: [
      {
        question: 'Can I use AI music in a commercial game?',
        answer:
          'Yes, music generated with Easy-Peasy.AI can be used in commercial games, including those sold on Steam, App Store, Google Play, and other platforms, subject to your subscription plan.',
      },
      {
        question: 'Can the AI create loopable game music?',
        answer:
          'The AI generates tracks that can be seamlessly looped in your game engine. The start and end of tracks are designed to flow naturally for continuous playback.',
      },
      {
        question: 'What game genres does the AI handle?',
        answer:
          'The AI can create music for RPGs, platformers, puzzle games, racing games, horror games, strategy games, visual novels, rhythm games, and any other genre you can imagine.',
      },
      {
        question: 'Can I generate different tracks for different game levels?',
        answer:
          'Absolutely. Generate unique tracks for each level, area, or game state. Describe the specific mood and energy for each track in your prompt to get varied, cohesive results.',
      },
    ],
    relatedSlugs: ['for-videos', 'electronic', 'epic-music', 'ambient'],
    promptExamples: [
      'Epic boss battle theme with dramatic orchestral strings, powerful brass, intense percussion, and a building sense of danger, D minor at 150 BPM',
      'Calm puzzle game music with soft piano melodies, gentle ambient pads, and a peaceful, focused atmosphere, C major at 80 BPM',
      'Retro 8-bit style adventure game music with chiptune melodies, bouncy bass, and an upbeat, adventurous feel, F major at 140 BPM',
    ],
    demoTrack: {
      name: 'Pixel Heartbeat',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/00f0aaa1-3f8c-4d05-a6ff-8c7c40acb0a8/63cc1118-d9e3-4e53-a4c5-16f315e3c153.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/for-games.jpeg',
      duration: '3:00',
      prompt:
        'EDM inspired by 90s and 00s, uplifting game music with pixel and retro vibes',
    },
  },
  {
    slug: 'for-podcasts',
    name: 'Music for Podcasts',
    category: 'use-case',
    seoTitle: 'AI Music Generator for Podcasts - Intro & Outro Music',
    seoDescription:
      'Create podcast intro and outro music with AI. Generate custom podcast theme music, background tracks, and transition sounds. Professional and unique.',
    heroHeading: 'AI Music Generator for Podcasts',
    heroSubheading:
      'Create a unique sonic identity for your podcast with custom intro music, outro themes, and background tracks that set you apart.',
    description:
      'Your podcast deserves a unique sound identity. Our AI music generator creates custom intro music, outro themes, background tracks, and transition sounds that perfectly match your podcast brand and tone. Whether you host a true crime show needing dark, suspenseful music, a comedy podcast wanting upbeat vibes, or a business show requiring professional underscore, get the perfect podcast music instantly.',
    icon: 'mic',
    gradient: 'from-green-600/20 to-emerald-600/20',
    faqs: [
      {
        question: 'Can I create a unique intro for my podcast?',
        answer:
          'Yes, generate a distinctive intro theme that becomes your podcast signature. Describe the energy, mood, and genre that matches your show, and the AI creates a custom piece that sets the tone.',
      },
      {
        question: 'How long should podcast intro music be?',
        answer:
          'Most podcast intros are 10-30 seconds. Generate a full track and trim it to the perfect length. You can also fade it in and out under your spoken intro.',
      },
      {
        question: 'Can I use different music for different segments?',
        answer:
          'Absolutely. Generate separate tracks for your intro, outro, ad breaks, segment transitions, and background music to create a polished, professional listening experience.',
      },
      {
        question: 'Will podcast platforms flag AI-generated music?',
        answer:
          'No, AI-generated music from Easy-Peasy.AI is royalty-free and will not be flagged by Spotify, Apple Podcasts, or other platforms for copyright issues.',
      },
    ],
    relatedSlugs: ['for-videos', 'lo-fi', 'ambient', 'pop'],
    promptExamples: [
      'Podcast intro music with energetic drums, bright guitar riff, and a catchy, memorable hook that builds excitement, C major at 120 BPM, 15 seconds',
      'Calm background music for a storytelling podcast, soft piano and gentle strings, minimal and unobtrusive, E-flat major at 70 BPM',
      'True crime podcast intro with dark, suspenseful piano, low drones, and a tense, mysterious atmosphere, B minor at 90 BPM',
    ],
    demoTrack: {
      name: 'Podcast Intro',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/5321e250-3ffb-4719-a494-5d322268bc63/4157159c-a811-4a75-a7be-a78e067f4792.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/for-podcasts.jpeg',
      duration: '0:53',
      prompt: 'Podcast intro with upbeat acoustic guitar and fiddle',
    },
  },
  {
    slug: 'for-meditation',
    name: 'Music for Meditation',
    category: 'use-case',
    seoTitle:
      'AI Meditation Music Generator - Create Calming Music for Mindfulness',
    seoDescription:
      'Generate meditation music with AI. Create calming tracks for meditation, yoga, sleep, and mindfulness practices. Peaceful soundscapes and healing frequencies.',
    heroHeading: 'AI Meditation Music Generator',
    heroSubheading:
      'Create deeply calming music for meditation, yoga, sleep, and mindfulness. Generate peaceful soundscapes that promote relaxation and inner peace.',
    description:
      'Our AI meditation music generator creates deeply calming compositions designed to support mindfulness practices. Generate gentle soundscapes for guided meditation, peaceful ambient music for yoga sessions, soothing tracks for sleep, or nature-infused music for deep relaxation. The AI understands the sonic qualities that promote calm, such as slow tempos, gentle dynamics, and harmonious tonalities.',
    icon: 'lotus',
    gradient: 'from-indigo-600/20 to-purple-600/20',
    faqs: [
      {
        question: 'What types of meditation music can the AI create?',
        answer:
          'The AI can generate guided meditation backgrounds, yoga flow music, sleep music, breathwork accompaniment, sound bath-style tracks, nature soundscapes, and deep relaxation ambient pieces.',
      },
      {
        question: 'Can I create music for my meditation app or channel?',
        answer:
          'Yes, the generated music is perfect for meditation apps, YouTube meditation channels, wellness platforms, and mindfulness courses. It is royalty-free based on your plan.',
      },
      {
        question: 'Can the AI include nature sounds in meditation tracks?',
        answer:
          'Yes, mention nature elements in your prompt such as rain, ocean waves, forest sounds, birdsong, or running water, and the AI will incorporate these calming elements.',
      },
      {
        question: 'What BPM works best for meditation music?',
        answer:
          'Meditation music typically ranges from 40-70 BPM, mimicking resting heart rate. Very slow, gentle tempos with minimal rhythmic elements work best for deep meditation.',
      },
    ],
    relatedSlugs: ['for-study', 'ambient', 'chill-music', 'classical'],
    promptExamples: [
      'Gentle meditation music with soft singing bowls, ethereal pads, and a deeply peaceful atmosphere, no defined key, very slow and spacious',
      'Yoga flow music with soft sitar-like tones, gentle tabla rhythm, warm ambient textures, and a calm, centering energy, D major at 60 BPM',
      'Sleep music with ultra-soft piano notes, warm ambient pads, gentle rain sounds, and a deeply soothing, drowsy atmosphere, C major at 50 BPM',
    ],
    demoTrack: {
      name: 'Cavern Hymn in G#',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/28372dd5-ec05-4939-96fa-f38dcb130cda/afede780-1dc9-4b5a-ae70-79ff468040da.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/for-meditation.jpeg',
      duration: '2:52',
      prompt:
        'Ritual dark ambient, meditative drone with Tibetan singing bowls',
    },
  },
  {
    slug: 'for-workout',
    name: 'Music for Workouts',
    category: 'use-case',
    seoTitle: 'AI Workout Music Generator - Create Gym & Fitness Tracks',
    seoDescription:
      'Generate workout music with AI. Create high-energy gym tracks, running music, HIIT playlists, and fitness class beats. Pump up your workouts.',
    heroHeading: 'AI Workout Music Generator',
    heroSubheading:
      'Generate high-energy tracks that fuel your workouts. From intense HIIT beats to steady running rhythms and powerful gym anthems.',
    description:
      'Elevate your fitness routine with custom AI-generated workout music. Create high-energy tracks for intense HIIT sessions, steady-tempo music for long runs, pumping gym anthems for weightlifting, or rhythmic beats for fitness classes. The AI generates motivating music with the right tempo and energy to keep you pushing through every rep and every mile.',
    icon: 'fire',
    gradient: 'from-orange-600/20 to-red-600/20',
    faqs: [
      {
        question: 'What BPM works best for different workout types?',
        answer:
          'Walking: 100-120 BPM, jogging: 120-140 BPM, running: 140-170 BPM, HIIT: 140-180 BPM, weightlifting: 120-145 BPM, yoga: 60-90 BPM. Specify the BPM in your prompt.',
      },
      {
        question: 'Can I create music for fitness classes I teach?',
        answer:
          'Yes, generate custom tracks for spinning, aerobics, Zumba, CrossFit, HIIT, or any other fitness class. The music is royalty-free and can be played in your sessions.',
      },
      {
        question: 'Can the AI match the energy progression of my workout?',
        answer:
          'You can generate separate tracks for warm-up, main workout, peak intensity, and cool-down phases. Describe the energy level for each phase in your prompts.',
      },
      {
        question: 'What music styles work best for workouts?',
        answer:
          'EDM, hip-hop, rock, pop, and drum and bass are popular workout genres. The AI can generate any of these styles with high energy levels perfect for exercise.',
      },
    ],
    relatedSlugs: ['electronic', 'hip-hop', 'rock', 'epic-music'],
    promptExamples: [
      'High-energy HIIT workout track with pounding bass, intense synth stabs, and relentless driving energy, A minor at 150 BPM',
      'Running music with a steady, motivating beat, uplifting synth melody, and a push-forward momentum, E minor at 140 BPM',
      'Heavy gym anthem with powerful bass drops, aggressive synths, and maximum hype energy for weightlifting, D minor at 130 BPM',
    ],
    demoTrack: {
      name: 'Step Up, Andrea',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/dc3a885a-368d-485d-acaa-f75b59e80aa8/dadb7dd9-3119-4b42-aa74-d3b0ef96c5e8.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/for-workout.jpeg',
      duration: '2:57',
      prompt:
        'High-energy pop workout groove with bright synth stabs and tight electronic drums',
    },
  },
  {
    slug: 'for-study',
    name: 'Music for Studying',
    category: 'use-case',
    seoTitle: 'AI Study Music Generator - Create Focus & Concentration Music',
    seoDescription:
      'Generate study music with AI. Create focus music, concentration tracks, and background study playlists. Boost productivity with the perfect study soundtrack.',
    heroHeading: 'AI Study Music Generator',
    heroSubheading:
      'Create the perfect study soundtrack with gentle, focus-enhancing music that helps you concentrate without distraction.',
    description:
      'Our AI study music generator creates background music specifically designed to enhance focus and concentration. Generate gentle lo-fi beats for late-night study sessions, calm ambient textures for deep work, soft classical-style pieces for reading, or minimal electronic music for coding. The music is intentionally subtle and repetitive, creating an optimal environment for learning without pulling attention away from your work.',
    icon: 'book',
    gradient: 'from-blue-600/20 to-indigo-600/20',
    faqs: [
      {
        question: 'Does study music actually help with concentration?',
        answer:
          'Research suggests that certain types of background music can improve focus and productivity, particularly music without lyrics, with steady tempos, and at moderate volumes. Our AI generates music with these characteristics.',
      },
      {
        question: 'What type of study music is most effective?',
        answer:
          'Lo-fi beats, ambient music, soft classical, and minimal electronic are among the most effective study music styles. They provide a consistent background without sudden changes that break concentration.',
      },
      {
        question: 'Can I create study music playlists?',
        answer:
          'Yes, generate multiple tracks with similar vibes to build a cohesive study playlist. You can create hours of unique focus music for different study modes.',
      },
      {
        question: 'Is this music good for work and coding too?',
        answer:
          'Absolutely. The focus-enhancing qualities of study music apply equally to professional work, coding, writing, reading, and any task requiring sustained concentration.',
      },
    ],
    relatedSlugs: ['lo-fi', 'ambient', 'for-meditation', 'chill-music'],
    promptExamples: [
      'Gentle study music with soft piano, minimal ambient pads, and a calm, focused atmosphere that helps concentration, C major at 70 BPM',
      'Lo-fi study beat with warm vinyl texture, soft Rhodes chords, quiet drums, and a cozy late-night library vibe, G minor at 75 BPM',
      'Minimal electronic focus music with subtle synth textures, soft clicks, and a steady, unobtrusive flow, A minor at 90 BPM',
    ],
    demoTrack: {
      name: "Working Man's Peace",
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/7162157c-85ef-4b98-9bdc-e3bba3c4481d/f21e3aa4-707f-45d9-b3dc-3e6f5cead305.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/for-study.jpeg',
      duration: '3:30',
      prompt:
        'Warm country ballad with brushed kit, steel guitar swells, and intimate vocals',
    },
  },
  {
    slug: 'for-film',
    name: 'Music for Film',
    category: 'use-case',
    seoTitle: 'AI Film Score Generator - Create Cinematic Music with AI',
    seoDescription:
      'Generate cinematic film music with AI. Create movie scores, trailer music, and dramatic soundtracks. From epic orchestral to intimate piano themes.',
    heroHeading: 'AI Film Score Generator',
    heroSubheading:
      'Create cinematic scores that elevate your visual storytelling. From epic orchestral arrangements to intimate piano themes and suspenseful underscores.',
    description:
      'Our AI film score generator creates cinematic music that brings visual stories to life. Generate sweeping orchestral scores for epic narratives, intimate piano themes for character moments, suspenseful underscores for thrillers, whimsical compositions for comedies, or atmospheric soundscapes for sci-fi worlds. Professional filmmakers and content creators alike can get studio-quality film music instantly.',
    icon: 'film',
    gradient: 'from-slate-600/20 to-purple-600/20',
    faqs: [
      {
        question: 'Can AI music replace a film composer?',
        answer:
          'AI music is an excellent tool for independent filmmakers, short films, web series, and projects with limited budgets. For major productions, it can serve as temp tracks, demos, or inspiration for human composers.',
      },
      {
        question: 'Can the AI create music that matches scene emotions?',
        answer:
          'Yes, describe the emotional arc of your scene, including tension, relief, joy, sadness, suspense, or triumph, and the AI will generate music that supports that emotional journey.',
      },
      {
        question: 'What cinematic music styles can the AI generate?',
        answer:
          'Epic orchestral, intimate piano, suspense/thriller, horror, romantic, sci-fi atmospheric, comedy/whimsical, action, and documentary styles are all supported.',
      },
      {
        question: 'Can I use AI film music for festival submissions?',
        answer:
          'Yes, the music is royalty-free and can be used in films submitted to festivals, streaming platforms, and theatrical releases based on your subscription plan.',
      },
    ],
    relatedSlugs: ['classical', 'epic-music', 'dramatic-music', 'ambient'],
    promptExamples: [
      'Epic cinematic score with full orchestra, building from quiet strings to a massive brass climax, triumphant and emotional, D major at 95 BPM',
      'Suspenseful thriller underscore with tense strings, low piano notes, and a building sense of dread and unease, chromatic, 70 BPM',
      'Whimsical comedy score with playful woodwinds, pizzicato strings, and a light, charming character, F major at 130 BPM',
    ],
    demoTrack: {
      name: 'Shadows Rise',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/60a4d8af-356e-4d9d-90e5-763009591355/5fe84ee9-4bba-438e-98c5-850c1653bcf4.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/for-film.jpeg',
      duration: '2:29',
      prompt:
        'Cinematic epic with sweeping strings, choir harmonies, and dramatic crescendos',
    },
  },

  // ==================== MOODS ====================
  {
    slug: 'chill-music',
    name: 'Chill',
    category: 'mood',
    seoTitle: 'AI Chill Music Generator - Create Relaxing & Chill Beats',
    seoDescription:
      'Generate chill music with AI. Create relaxing beats, downtempo tracks, and smooth chill-out music for unwinding, studying, and background listening.',
    heroHeading: 'AI Chill Music Generator',
    heroSubheading:
      'Create smooth, relaxing tracks for unwinding, studying, or background listening. Laid-back beats, warm textures, and easy vibes.',
    description:
      'Our AI chill music generator creates the perfect soundtrack for relaxation and easy listening. Generate downtempo beats with warm bass and smooth melodies, chill-out electronica with floating textures, laid-back acoustic pieces for lazy afternoons, or ambient chill for background ambiance. The music is designed to create a calm, pleasant atmosphere without demanding attention.',
    icon: 'cloud',
    gradient: 'from-teal-600/20 to-blue-600/20',
    faqs: [
      {
        question: 'What is chill music exactly?',
        answer:
          'Chill music is a broad category encompassing relaxing, downtempo, and laid-back music across genres. It includes chill-hop, downtempo electronic, soft acoustic, ambient, and smooth jazz, all sharing a relaxed, easygoing feel.',
      },
      {
        question: 'What is the typical BPM for chill music?',
        answer:
          'Chill music typically ranges from 60-100 BPM. The slower tempos create a relaxed feel, though some chill electronic tracks can be up to 110 BPM with a laid-back groove.',
      },
      {
        question: 'Is chill music good for cafes and retail stores?',
        answer:
          'Yes, chill music is one of the most popular choices for cafes, boutiques, co-working spaces, and retail environments. It creates a welcoming atmosphere that encourages customers to linger.',
      },
      {
        question: 'Can I create chill music for my streaming playlist?',
        answer:
          'Absolutely. Generate unique chill tracks and build your own curated playlist. The royalty-free nature means you can distribute them on streaming platforms based on your plan.',
      },
    ],
    relatedSlugs: ['lo-fi', 'ambient', 'for-study', 'jazz'],
    promptExamples: [
      'Smooth chill beat with warm electric piano, soft bass groove, gentle hi-hats, and a relaxing sunset vibes atmosphere, G major at 85 BPM',
      'Downtempo electronic with floating synth pads, subtle beats, and a spacious, dreamy chill-out feel, D minor at 90 BPM',
      'Chill acoustic with fingerpicked guitar, soft percussion, and a warm, lazy afternoon atmosphere, C major at 80 BPM',
    ],
    demoTrack: {
      name: "Whatever's Clever",
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/78379775-b73e-43ae-bfb0-c6524b9b4588/360830f7-070e-416a-a8d4-6893b04d3172.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/chill-music.jpeg',
      duration: '2:15',
      prompt:
        'Laid-back deep house groove with mellow hip-hop drums and hazy Rhodes chords',
    },
  },
  {
    slug: 'epic-music',
    name: 'Epic',
    category: 'mood',
    seoTitle: 'AI Epic Music Generator - Create Powerful & Epic Tracks',
    seoDescription:
      'Generate epic music with AI. Create powerful orchestral tracks, cinematic battle themes, and triumphant compositions for trailers, games, and videos.',
    heroHeading: 'AI Epic Music Generator',
    heroSubheading:
      'Create powerful, awe-inspiring tracks with massive orchestral arrangements, thundering percussion, and triumphant energy that moves audiences.',
    description:
      'Our AI epic music generator creates larger-than-life compositions that inspire awe and excitement. Generate thundering battle themes with massive percussion and soaring brass, triumphant victory anthems with full orchestra, dramatic trailer music with building intensity, or heroic adventure themes with sweeping strings. Perfect for trailers, games, motivational content, and any project that needs the power to move an audience.',
    icon: 'crown',
    gradient: 'from-amber-600/20 to-red-600/20',
    faqs: [
      {
        question: 'What makes music sound epic?',
        answer:
          'Epic music typically features large orchestral arrangements, powerful brass sections, thundering drums and percussion, sweeping string melodies, dynamic build-ups, and dramatic climaxes. The combination creates a sense of grandeur and emotional intensity.',
      },
      {
        question: 'Can I use epic music for YouTube video intros?',
        answer:
          'Absolutely. Epic music is perfect for YouTube intros, channel trailers, highlight reels, and any content where you want to make a powerful first impression.',
      },
      {
        question: 'Is epic music suitable for game trailers?',
        answer:
          'Yes, epic orchestral music is the standard for game trailers and cinematic sequences. Generate tracks that build tension and deliver powerful climaxes.',
      },
      {
        question: 'Can the AI create epic music with choir elements?',
        answer:
          'Yes, describe choral elements in your prompt such as Latin choir, ethereal voices, or choir crescendo, and the AI will incorporate vocal-style elements into the composition.',
      },
    ],
    relatedSlugs: ['classical', 'for-film', 'dramatic-music', 'metal'],
    promptExamples: [
      'Epic orchestral trailer music with massive brass, thundering taiko drums, sweeping strings, and a building climax to a powerful finale, D minor at 120 BPM',
      'Heroic adventure theme with soaring French horns, driving percussion, and a triumphant, inspirational melody, A major at 130 BPM',
      'Epic battle music with aggressive strings, pounding war drums, intense brass stabs, and relentless momentum, E minor at 150 BPM',
    ],
    demoTrack: {
      name: 'The Battle of Qarqar',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/bc2e60a8-0b02-499a-88ab-4c4c12730673/c15df4d0-91b4-49ef-b1bc-d42fbeeae70e.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/epic-music.jpeg',
      duration: '4:12',
      prompt:
        'Cinematic Middle Eastern metal with war drums, brass stabs, and choir swells',
    },
  },
  {
    slug: 'sad-music',
    name: 'Sad',
    category: 'mood',
    seoTitle: 'AI Sad Music Generator - Create Emotional & Melancholic Music',
    seoDescription:
      'Generate sad music with AI. Create emotional piano pieces, melancholic soundtracks, and heartfelt compositions for films, videos, and personal projects.',
    heroHeading: 'AI Sad Music Generator',
    heroSubheading:
      'Create deeply emotional compositions with melancholic melodies, poignant harmonies, and heartfelt musical expressions that touch the soul.',
    description:
      'Our AI sad music generator creates emotionally moving compositions that convey sadness, nostalgia, loss, and bittersweet beauty. Generate delicate piano pieces with aching melodies, sorrowful string arrangements with expressive dynamics, melancholic ambient soundscapes, or cinematic emotional underscores. Perfect for dramatic film scenes, memorial videos, emotional storytelling, and any project that needs to evoke deep feelings.',
    icon: 'droplet',
    gradient: 'from-blue-800/20 to-slate-600/20',
    faqs: [
      {
        question: 'What musical elements make music sound sad?',
        answer:
          'Sad music typically uses minor keys, slow tempos, descending melodic lines, sparse arrangements, and instruments like piano, strings, and cello. Dynamic contrasts between soft and slightly louder passages add emotional depth.',
      },
      {
        question: 'Can sad music be beautiful too?',
        answer:
          'Absolutely. Some of the most beautiful music ever composed is melancholic. The AI creates pieces that are deeply sad yet artistically beautiful, with rich harmonies and elegant melodies.',
      },
      {
        question: 'Is sad music good for memorial or tribute videos?',
        answer:
          'Yes, tasteful, melancholic instrumental music is perfect for memorial videos, tribute compilations, in memoriam segments, and remembrance content.',
      },
      {
        question: 'Can I specify how intense the sadness should be?',
        answer:
          'Yes, you can describe the degree of emotion in your prompt, from gently wistful and bittersweet to deeply sorrowful and heart-wrenching, and the AI will adjust accordingly.',
      },
    ],
    relatedSlugs: ['classical', 'ambient', 'for-film', 'dramatic-music'],
    promptExamples: [
      'Melancholic piano piece with a simple, aching melody, gentle dynamics, and a profound sense of loss and beauty, A minor at 60 BPM',
      'Sorrowful orchestral score with expressive cello solo, gentle strings, and a heartbreaking, cinematic emotional quality, F minor at 55 BPM',
      'Bittersweet acoustic guitar piece with a nostalgic, reflective atmosphere and a gentle sense of longing, E minor at 70 BPM',
    ],
    demoTrack: {
      name: 'Paper Hearts',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/69a0fe65-c131-4ddf-89a3-aabe3b68bfd9/f5e51d8f-4c6c-4298-bf2e-d720f88ef298.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/sad-music.jpeg',
      duration: '2:54',
      prompt:
        'Melancholic piano-driven track with soft strings, slow and emotional',
    },
  },
  {
    slug: 'happy-music',
    name: 'Happy',
    category: 'mood',
    seoTitle: 'AI Happy Music Generator - Create Upbeat & Joyful Tracks',
    seoDescription:
      'Generate happy music with AI. Create upbeat, joyful, and feel-good tracks for celebrations, ads, content creation, and spreading positive vibes.',
    heroHeading: 'AI Happy Music Generator',
    heroSubheading:
      'Create upbeat, joyful tracks that radiate positive energy and put smiles on faces. Perfect for celebrations, ads, and feel-good content.',
    description:
      'Our AI happy music generator creates infectious, feel-good compositions that brighten any project. Generate upbeat pop with catchy melodies, bouncy acoustic tracks with clapping rhythms, cheerful ukulele pieces with whistling melodies, or lively orchestral arrangements with playful energy. Perfect for commercials, children content, celebrations, corporate videos, and any time you need music that radiates joy.',
    icon: 'sparkle',
    gradient: 'from-yellow-500/20 to-orange-500/20',
    faqs: [
      {
        question: 'What makes music sound happy?',
        answer:
          'Happy music typically features major keys, upbeat tempos (100-140 BPM), bright instrumentation, ascending melodies, and rhythmic patterns that create a sense of energy and positivity.',
      },
      {
        question: 'Is happy music good for advertisements?',
        answer:
          'Yes, upbeat and positive music is one of the most popular choices for ads, commercials, and brand content. It creates positive associations and encourages viewer engagement.',
      },
      {
        question: 'Can I create children-friendly happy music?',
        answer:
          'Absolutely. Generate playful, bouncy tracks with ukulele, xylophone, whistling, and clapping that are perfect for children content, educational videos, and family-friendly projects.',
      },
      {
        question: 'What instruments create the happiest sound?',
        answer:
          'Ukulele, acoustic guitar, piano, whistling, clapping, bright brass, glockenspiel, and strings played pizzicato are commonly associated with happy, uplifting music.',
      },
    ],
    relatedSlugs: ['pop', 'folk', 'chill-music', 'for-videos'],
    promptExamples: [
      'Cheerful ukulele track with handclaps, whistling melody, and a bright, sunny, feel-good atmosphere, C major at 120 BPM',
      'Upbeat pop with catchy piano hook, bouncy bass, energetic drums, and an irresistibly positive, danceable energy, G major at 128 BPM',
      'Playful orchestral piece with pizzicato strings, bouncy woodwinds, xylophone, and a whimsical, joyful character, F major at 130 BPM',
    ],
    demoTrack: {
      name: 'The Home Office Dream',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/5914dcfa-db69-4842-a855-3bdb65d726fa/7328cbd1-4016-4750-b5da-6e2c7fae4f95.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/happy-music.jpeg',
      duration: '1:50',
      prompt:
        'Happy Irish folk with lively fiddle, bodhran, and fingerstyle guitar',
    },
  },
  {
    slug: 'dramatic-music',
    name: 'Dramatic',
    category: 'mood',
    seoTitle: 'AI Dramatic Music Generator - Create Intense & Cinematic Tracks',
    seoDescription:
      'Generate dramatic music with AI. Create intense cinematic compositions, suspenseful scores, and tension-building tracks for films, games, and videos.',
    heroHeading: 'AI Dramatic Music Generator',
    heroSubheading:
      'Create intense, tension-filled compositions with building suspense, powerful crescendos, and emotionally charged musical storytelling.',
    description:
      'Our AI dramatic music generator creates intense, emotionally charged compositions that heighten tension and impact. Generate suspenseful scores with building dread, powerful crescendos that deliver emotional payoff, intense action underscores with driving momentum, or dark atmospheric pieces that create unease. Essential for thrillers, horror content, intense game moments, and any visual storytelling that needs to grip the audience.',
    icon: 'bolt',
    gradient: 'from-red-800/20 to-purple-800/20',
    faqs: [
      {
        question: 'What is the difference between dramatic and epic music?',
        answer:
          'While both are intense, dramatic music focuses on tension, suspense, and emotional conflict, while epic music emphasizes grandeur, triumph, and heroism. Dramatic music often builds toward resolution; epic music celebrates power and victory.',
      },
      {
        question: 'Can dramatic music work for corporate presentations?',
        answer:
          'Yes, when used appropriately. Moderate dramatic music can add weight and importance to key presentation moments, product reveals, or significant announcements.',
      },
      {
        question: 'Is dramatic music suitable for horror content?',
        answer:
          'Absolutely. Dramatic music with dark, tense qualities is essential for horror films, games, and content. Specify horror elements in your prompt for tracks with suspense, dread, and unsettling atmospheres.',
      },
      {
        question: 'Can the AI create music with building tension?',
        answer:
          'Yes, describe a building arc in your prompt, such as starting quiet and building to an intense climax. The AI understands musical tension and release and can create dynamic compositions.',
      },
    ],
    relatedSlugs: ['epic-music', 'sad-music', 'for-film', 'classical'],
    promptExamples: [
      'Suspenseful dramatic score starting with quiet, tense strings, building slowly with added layers to an intense, heart-pounding climax, C minor at 85 BPM',
      'Dark dramatic music with low piano notes, unsettling dissonant strings, and a creeping sense of dread and danger, chromatic, 65 BPM',
      'Action dramatic score with driving percussion, urgent strings, brass stabs, and relentless forward momentum, D minor at 140 BPM',
    ],
    demoTrack: {
      name: 'El Ultimo Round',
      audioUrl:
        'https://api.easy-peasy.ai/storage/v1/object/public/tts/66ae9a1c-e991-46fc-b40e-961f6f520bbf/4ac8bf9a-1520-455c-9f8f-b66a2b51624c.mp3',
      imageUrl: 'https://media.easy-peasy.ai/music-genres/dramatic-music.jpeg',
      duration: '2:22',
      prompt:
        'Orchestral epic with dramatic vocals, driving strings, and intense percussion',
    },
  },
];
